import { Tag, type TagColor } from '@fountain/ripple/tag';
import { makeStyles, Typography } from '@material-ui/core';
import React, { FC, memo } from 'react';
import { useIntl } from 'react-intl';

import ApplicantTableV2Cell from 'containers/ApplicantTableV2/components/ApplicantTableV2Cell';
import { COLUMN_TYPES } from 'containers/ApplicantTableV2/constants';
import { Applicant } from 'containers/ApplicantTableV2/types';
import {
  RECOMMENDATION_LABELS,
  RecommendationKey,
} from 'containers/ScorecardModals/ScorecardResults/scorecardResults';

const EMPTY_STATE = '—';

const RECOMMENDATION_ORDER: RecommendationKey[] = [
  'strong_no',
  'no',
  'yes',
  'strong_yes',
];

const RECOMMENDATION_COLOR: Record<RecommendationKey, TagColor> = {
  strong_no: 'rose',
  no: 'rose',
  yes: 'green',
  strong_yes: 'green',
};

const useStyles = makeStyles({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
});

export interface ApplicantTableV2ScorecardScoreCellProps {
  applicant: Applicant;
}

// The applicant's average score across all submitted scorecard evaluations,
// followed by one tag per recommendation that has at least one submission. An
// applicant with no submitted scorecards renders the table's em-dash empty
// state.
export const ApplicantTableV2ScorecardScoreCell: FC<
  ApplicantTableV2ScorecardScoreCellProps
> = ({ applicant }) => {
  const classes = useStyles();
  const { formatMessage, formatNumber } = useIntl();
  const score = applicant.score_card_score;
  const counts = applicant.score_card_recommendations;
  const recommendations = RECOMMENDATION_ORDER.filter(
    key => (counts?.[key] ?? 0) > 0,
  );

  if (score == null && recommendations.length === 0) {
    return (
      <ApplicantTableV2Cell columnType={COLUMN_TYPES.SCORECARD_SCORE}>
        <Typography variant="caption" align="left" display="block">
          {EMPTY_STATE}
        </Typography>
      </ApplicantTableV2Cell>
    );
  }

  return (
    <ApplicantTableV2Cell columnType={COLUMN_TYPES.SCORECARD_SCORE}>
      <div className={classes.row}>
        {score != null && (
          <Typography variant="caption" align="left">
            {formatNumber(score, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 2,
            })}
          </Typography>
        )}
        {recommendations.map(key => (
          <Tag key={key} color={RECOMMENDATION_COLOR[key]} rounded={false}>
            {`${formatMessage(RECOMMENDATION_LABELS[key])} - ${formatNumber(
              counts?.[key] ?? 0,
            )}`}
          </Tag>
        ))}
      </div>
    </ApplicantTableV2Cell>
  );
};

export default memo(ApplicantTableV2ScorecardScoreCell);
